<template>
	<div>
		<b-form-row>
			<b-col cols="4">
				<b-form-select v-model="serviceId" :options="[ { id: null, name: 'All' }, ...services ]" value-field="id" text-field="name"></b-form-select>
			</b-col>
			<b-col>
				<b-form-input placeholder="Search..." v-model="q" class="mb-2" />
			</b-col>
		</b-form-row>
		<b-table :items="giftTypes" :fields="[{ key: 'type', label: 'Donation type' }, 'value', 'media', 'details', { key: 'actions', thClass: 'text-right', tdClass: 'text-right'}]" small striped :busy="loading>0">
			<template #cell(type)="data">
				<span class="mr-2">
					<a href="#" @click.prevent="playTestAlert(data.item, $event)" v-b-tooltip.hover.left title="Play test alert"><b-icon icon="play-fill"></b-icon></a>
				</span>
				<b-avatar size="3em" :src="data.item.media?.find(m => m.mime?.[0]=='image')?.url||data.item.image||data.item.image_url" badge-variant="none" square class="mr-2">
					<template #badge><b-avatar size="1.5em" :src="data.item.service_icon?.url" v-if="data.item.service_icon?.url"/></template>
				</b-avatar>
				<div class="d-inline-block align-middle">
					{{data.item.name||data.item.gift_type}}<br />
					<small class="text-muted">{{data.item.service_name}}</small>
				</div>
			</template>
			<template #cell(value)="data">
				<b-badge v-if="data.item.value && data.item.currency" :style="giftThresholdStyle(data.item)">{{valueBadgeText(data.item)}}</b-badge>
			</template>
			<template #cell(media)="data">
				<b-avatar-group size="1.5rem" rounded overlap=".05">
					<b-avatar v-if="data.item.media?.filter(m => m.mime?.[0]=='image')?.length" :src="data.item.media.filter(m => m.mime?.[0]=='image')[0].url" v-b-tooltip.hover.bottom.v-info :title="`Has ${data.item.media.filter(m => m.mime?.[0]=='image').length} image(s) configured`" />
					<b-avatar v-if="data.item.media?.filter(m => m.mime?.[0]=='audio')?.length" v-b-tooltip.hover.bottom.v-info :title="`Has ${data.item.media.filter(m => m.mime?.[0]=='audio').length} sound(s) configured`"><b-icon icon="music-note-beamed" class="text-muted"></b-icon></b-avatar>
				</b-avatar-group>
			</template>
			<template #cell(details)="data">
				<span class="text-muted">{{[data.item.string, data.item.string_multi].filter(Boolean).join(' / ')}}</span><br />
				<b-badge v-if="data.item.group" variant="success" class="ml-1">group</b-badge>
			</template>
			<template #cell(actions)="data">
				<a href="#" @click.prevent="$refs.modal.show(data.item.service_id, data.item.gift_type)" v-b-tooltip.hover.right title="Edit donation event"><b-icon icon="pencil-fill"></b-icon></a>
			</template>
		</b-table>
		<pager :per-page="perPage" :skip="skip" :total="total" @change="setPage"></pager>
		<b-alert variant="danger" :show="!!error">{{error}}</b-alert>
		<dono-media-modal ref="modal" @saved="loadGiftTypes" />
	</div>
</template>

<script>
import debounce from 'debounce';
import Pager from '@/components/Pager.vue';
import DonoMediaModal from './Modal/DonoMedia.vue';
import giftColorThresholds from '@/GiftThresholds';
import util from '@/util.js';

export default {
	data() { return {
		loading: 0,
		modalLoading: 0,
		error: null,
		services: [],
		curUserGiftType: null,
		giftTypes: [],
		total: 0,
		skip: 0,
		perPage: 10,
		serviceId: null,
		q: '',
	}},
	components: {
		Pager,
		DonoMediaModal,
	},
	async mounted() {
		try {
			this.loading++;
			this.error = null;

			this.serviceId = parseInt(this.$route.query.serviceId)||null;
			this.q = this.$route.query.q||'';
			this.skip = parseInt(this.$route.query.skip||0)||0;
			this.perPage = parseInt(this.$route.query.perPage||10)||10;

			this.giftTypes = [];
			await Promise.all([
				this.loadGiftTypes(),
				this.loadServices(),
			]);

			this.$watch('$route', $route => {
				this.skip = parseInt($route.query.skip||0)||0;
				this.perPage = parseInt($route.query.perPage||10)||10;
				this.reloadGiftTypes();
			});
			this.$watch('q', debounce(q => this.$router.push({
				path: this.$route.path,
				query: {
					...this.$route.query,
					skip: undefined,
					q: q||undefined,
				},
			}), 300));
			this.$watch('serviceId', serviceId => this.$router.push({
				path: this.$route.path,
				query: {
					...this.$route.query,
					skip: undefined,
					serviceId: serviceId||undefined,
				},
			}));
		} catch(e) {
			console.error(e);
			this.error = e;
		} finally {
			this.loading--;
		}
	},
	methods: {
		setPage($event) {
			this.$router.push({
				path: this.$route.path,
				query: {
					...this.$route.query,
					skip: $event.skip || undefined,
					perPage: $event.perPage == 10 ? undefined : $event.perPage,
				},
			});
		},

		async loadServices() {
			const services = await this.$api.get('services');
			this.services = services.filter(s => s.capabilities.indexOf('chat') !== -1);
		},

		async loadGiftTypes() {
			const result = await this.$api.get('userGiftTypes', {
				q: this.$route.query.q||'',
				serviceId: parseInt(this.$route.query.serviceId)||'',
				skip: this.skip,
				limit: this.perPage,
			});
			this.giftTypes = result.results;
			this.total = result.total;
		},

		async reloadGiftTypes() {
			try {
				this.loading++;
				this.error = null;
				await this.loadGiftTypes();
			} catch(e) {
				console.error(e);
				this.error = e;
			} finally {
				this.loading--;
			}
		},

		valueBadgeText(item) {
			const multiplier = item.multiplier || 1;
			let text = util.generateValueString(item.value * multiplier, item.currency);
			if(item.converted_value != null && item.user_currency && item.user_currency !== item.currency)
				text += ' / ' + util.generateValueString(item.converted_value * multiplier, item.user_currency);
			return text;
		},

		giftThresholdStyle(item) {
			const threshold = util.getGiftThreshold(item.converted_value ?? item.value, giftColorThresholds);
			if(!threshold) return {};
			return {
				backgroundColor: threshold.color,
				color: util.getContrastingTextColor(threshold.color),
				border: 'none',
			};
		},

		async playTestAlert(item, event) {
			try {
				await this.$api.post('alerts/test', { type: 'gift', data: { serviceId: item.service_id, giftType: item.gift_type, currency: item.currency, multiplier: event.shiftKey ? undefined : 1 } });
			} catch(e) {
				console.error(e);
				this.$bvToast.toast('Could not send test alert: '+(e.error||String(e)), { title: 'Error', variant: 'danger', solid: true });
			}
		},
	},
};
</script>
